import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabaseClient } from "../../lib/supabase";
import { Project } from "../../interfaces";
import { LoadingScreen } from "../common/LoadingScreen";
import { Edit, Trash, ArrowLeft, Globe, BarChart2, PenSquare, ChevronDown, ChevronUp, ArrowUpDown, Save, X, ExternalLink, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from '../../lib/toast';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectPreview } from '../common/ProjectPreview';

interface Analysis {
  id: string;
  name: string;
  keyword: string;
  url?: string;
  quick_score?: number;
  status_type: 'task is ready' | 'need optimization' | 'all right' | 'made optimization' | 'on approving' | 'on site';
  created_at: string;
  serp_results?: any[];
  content_analysis?: any;
  docs_url?: string;
  editor_content?: string;
  text_score?: number | null;
}

interface ProjectUser {
  user_id: string;
  role: string;
  profiles?: {
    email: string;
    full_name: string | null;
  } | null;
}

interface ProjectWithUsers extends Project {
  project_users?: ProjectUser[];
}

type SortField = 'text_score' | 'created_at' | 'status_type';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 20;

const STATUS_TYPES = [
  'task is ready',
  'need optimization',
  'all right',
  'made optimization',
  'on approving',
  'on site'
];

export function ProjectShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<ProjectWithUsers | null>(null);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [editingDocsUrl, setEditingDocsUrl] = useState<string | null>(null);
  const [editingUrl, setEditingUrl] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();

  const userRole = 
    user?.user_metadata?.role || 
    user?.role ||
    (user?.email === 'andmaillev@gmail.com' ? 'admin' : 'client');

  const canEditProject = userRole === 'admin' || 
                        user?.email === 'andmaillev@gmail.com' || 
                        project?.owner_id === user?.id ||
                        (userRole === 'optimizer' && project?.project_users?.some(pu => 
                          pu.user_id === user?.id && pu.role === 'optimizer'
                        ));

  const canDeleteProject = userRole === 'admin' || 
                          user?.email === 'andmaillev@gmail.com' || 
                          project?.owner_id === user?.id;

  const canDeleteAnalyses = userRole === 'admin' || userRole === 'optimizer' || user?.email === 'andmaillev@gmail.com';

  useEffect(() => {
    if (!id) return;

    const subscription = supabaseClient
      .channel('analyses-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'analyses',
          filter: `project_id=eq.${id}`
        },
        (payload) => {
          setAnalyses(currentAnalyses => 
            currentAnalyses.map(analysis => 
              analysis.id === payload.new.id 
                ? { ...analysis, ...payload.new }
                : analysis
            )
          );
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id]);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const { data: projectData, error: projectError } = await supabaseClient
          .from("projects")
          .select(`
            *,
            project_users (
              user_id,
              role,
              profiles (
                email,
                full_name
              )
            )
          `)
          .eq("id", id)
          .single();

        if (projectError) throw projectError;
        setProject(projectData);

        const { data: analysesData, error: analysesError } = await supabaseClient
          .from("analyses")
          .select("id,name,keyword,url,quick_score,status_type,created_at,docs_url,text_score")
          .eq("project_id", id)
          .order("created_at", { ascending: false });

        if (analysesError) throw analysesError;
        setAnalyses(analysesData || []);

      } catch (err) {
        console.error("Error loading project:", err);
        setError(err instanceof Error ? err.message : "Failed to load project");
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatusFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Memoize filtered and sorted analyses to avoid recalculating on every render
  const sortedAndFilteredAnalyses = useMemo(() => {
    // First filter
    let filtered = analyses;
    
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(analysis =>
        analysis.name.toLowerCase().includes(lowerSearchTerm) ||
        analysis.keyword.toLowerCase().includes(lowerSearchTerm)
      );
    }

    if (selectedStatusFilter !== 'all') {
      filtered = filtered.filter(analysis => analysis.status_type === selectedStatusFilter);
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      if (sortField === 'text_score') {
        const scoreA = a.text_score ?? -1;
        const scoreB = b.text_score ?? -1;
        return sortDirection === 'asc' ? scoreA - scoreB : scoreB - scoreA;
      } else if (sortField === 'status_type') {
        const statusA = STATUS_TYPES.indexOf(a.status_type);
        const statusB = STATUS_TYPES.indexOf(b.status_type);
        return sortDirection === 'asc' ? statusA - statusB : statusB - statusA;
      } else {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });
  }, [analyses, searchTerm, selectedStatusFilter, sortField, sortDirection]);

  // Memoize paginated analyses
  const paginatedAnalyses = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedAndFilteredAnalyses.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedAndFilteredAnalyses, currentPage]);

  // Memoize total pages calculation
  const totalPages = useMemo(() => 
    Math.ceil(sortedAndFilteredAnalyses.length / ITEMS_PER_PAGE),
    [sortedAndFilteredAnalyses.length]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of table when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveName = async (analysisId: string) => {
    if (!editingValue.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    try {
      const { error } = await supabaseClient
        .from('analyses')
        .update({ name: editingValue.trim() })
        .eq('id', analysisId);

      if (error) throw error;

      setAnalyses(prev => prev.map(analysis => 
        analysis.id === analysisId 
          ? { ...analysis, name: editingValue.trim() }
          : analysis
      ));
      setEditingName(null);
      setEditingValue("");
      toast.success('Name updated successfully');
    } catch (err) {
      console.error('Error updating name:', err);
      toast.error('Failed to update name');
    }
  };

  const handleSaveStatus = async (analysisId: string, newStatus: string) => {
    try {
      const { error } = await supabaseClient
        .from('analyses')
        .update({ status_type: newStatus })
        .eq('id', analysisId);

      if (error) throw error;

      setAnalyses(prev => prev.map(analysis => 
        analysis.id === analysisId 
          ? { ...analysis, status_type: newStatus as Analysis['status_type'] }
          : analysis
      ));
      setEditingStatus(null);
      toast.success('Status updated successfully');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  const handleSaveDocsUrl = async (analysisId: string, url: string) => {
    try {
      const { error } = await supabaseClient
        .from('analyses')
        .update({ docs_url: url.trim() })
        .eq('id', analysisId);

      if (error) throw error;

      setAnalyses(prev => prev.map(analysis => 
        analysis.id === analysisId 
          ? { ...analysis, docs_url: url.trim() }
          : analysis
      ));
      setEditingDocsUrl(null);
      setEditingValue("");
      toast.success('Docs URL updated successfully');
    } catch (err) {
      console.error('Error updating docs URL:', err);
      toast.error('Failed to update docs URL');
    }
  };

  const handleSaveUrl = async (analysisId: string, url: string) => {
    try {
      const { error } = await supabaseClient
        .from('analyses')
        .update({ url: url.trim() })
        .eq('id', analysisId);

      if (error) throw error;

      setAnalyses(prev => prev.map(analysis => 
        analysis.id === analysisId 
          ? { ...analysis, url: url.trim() }
          : analysis
      ));
      setEditingUrl(null);
      setEditingValue("");
      toast.success('URL updated successfully');
    } catch (err) {
      console.error('Error updating URL:', err);
      toast.error('Failed to update URL');
    }
  };

  const handleCancelEdit = () => {
    setEditingName(null);
    setEditingValue("");
    setEditingStatus(null);
    setEditingDocsUrl(null);
    setEditingUrl(null);
  };

  const startEditing = (analysis: Analysis) => {
    setEditingName(analysis.id);
    setEditingValue(analysis.name);
  };

  const startEditingDocsUrl = (analysis: Analysis) => {
    setEditingDocsUrl(analysis.id);
    setEditingValue(analysis.docs_url || '');
  };

  const handleDeleteAnalysis = async (analysisId: string) => {
    if (!canDeleteAnalyses) {
      toast.error('You do not have permission to delete analyses');
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this analysis?");
    if (!confirmed) return;

    try {
      const { error } = await supabaseClient
        .from("analyses")
        .delete()
        .eq("id", analysisId);

      if (error) throw error;
      
      setAnalyses(analyses.filter(a => a.id !== analysisId));
      toast.success('Analysis deleted successfully');
    } catch (err) {
      console.error("Error deleting analysis:", err);
      toast.error('Failed to delete analysis');
    }
  };

  const handleDeleteProject = async () => {
    if (!canDeleteProject) {
      toast.error('You do not have permission to delete this project');
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this project? This action cannot be undone.");
    if (!confirmed) return;

    try {
      const { error } = await supabaseClient
        .from("projects")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast.success('Project deleted successfully');
      navigate("/projects");
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error('Failed to delete project');
    }
  };

  const getTextScoreColor = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'bg-gray-100 text-gray-800';
    if (score >= 75) return 'bg-green-100 text-green-800';
    if (score >= 50) return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  // Handle project preview - get URL from first analysis with URL
  const handlePreviewProject = () => {
    const analysisWithUrl = analyses.find(a => a.url);
    if (analysisWithUrl?.url) {
      // Open in new tab
      window.open(analysisWithUrl.url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('No URL found in analyses to preview');
    }
  };

  const getTextScoreDisplay = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'N/A';
    return `${score}%`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'task is ready':
        return 'bg-blue-100 text-blue-800';
      case 'need optimization':
        return 'bg-red-100 text-red-800';
      case 'all right':
        return 'bg-green-100 text-green-800';
      case 'made optimization':
        return 'bg-purple-100 text-purple-800';
      case 'on approving':
        return 'bg-yellow-100 text-yellow-800';
      case 'on site':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDescriptionPreview = (description: string) => {
    if (!description) return '';
    
    const words = description.split(/\s+/);
    return showFullDescription ? description : words.slice(0, 10).join(' ') + (words.length > 10 ? '...' : '');
  };

  const truncateKeyword = (keyword: string) => {
    if (keyword.length <= 15) {
      return keyword;
    }
    return keyword.substring(0, 15) + '...';
  };

  if (loading) return <LoadingScreen />;
  if (!project) return <div>Project not found</div>;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">{project.name}</h2>
          <div className="flex gap-2">
            {/* Preview button - show if there's any analysis with URL */}
            {analyses.some(a => a.url) && (
              <button
                onClick={handlePreviewProject}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                title="Preview website from first analysis with URL"
              >
                <Globe className="h-4 w-4 mr-2" />
                Preview
              </button>
            )}
            {canEditProject && (
              <button
                onClick={() => navigate(`/projects/edit/${project.id}`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </button>
            )}
            {canDeleteProject && (
              <button
                onClick={handleDeleteProject}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete Project
              </button>
            )}
            <button
              onClick={() => navigate(`/projects/${project.id}/analyses/create`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Analysis
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <div className="prose max-w-none mb-8">
          <div className="text-gray-600">
            {getDescriptionPreview(project.description || "No description provided.")}
            {project.description && project.description.split('\n').length > 1 && (
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="ml-2 text-indigo-600 hover:text-indigo-800 inline-flex items-center"
              >
                {showFullDescription ? (
                  <>
                    Show Less
                    <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Read More
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Analyses</h3>
            <button
              onClick={() => navigate(`/projects/${id}/analyses/create`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Analysis
            </button>
          </div>

          <div className="mb-4 space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or keyword..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2" />
                </div>
              </div>
              <div className="w-64">
                <select
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="all">All Statuses</option>
                  {STATUS_TYPES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Display total count */}
          <div className="mb-4 text-sm text-gray-500">
            Total: {sortedAndFilteredAnalyses.length} analyses
          </div>

          {paginatedAnalyses.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        Keyword
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('text_score')}
                      >
                        <div className="flex items-center">
                          Text Score
                          <ArrowUpDown className={`ml-1 h-4 w-4 ${sortField === 'text_score' ? 'text-indigo-500' : 'text-gray-400'}`} />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center">
                          Date
                          <ArrowUpDown className={`ml-1 h-4 w-4 ${sortField === 'created_at' ? 'text-indigo-500' : 'text-gray-400'}`} />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('status_type')}
                      >
                        <div className="flex items-center">
                          Status
                          <ArrowUpDown className={`ml-1 h-4 w-4 ${sortField === 'status_type' ? 'text-indigo-500' : 'text-gray-400'}`} />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Docs URL
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedAnalyses.map((analysis) => (
                      <tr key={analysis.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-64">
                          {editingName === analysis.id ? (
                            <div className="flex items-center space-x-2 w-full">
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                className="block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <button
                                onClick={() => handleSaveName(analysis.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span
                                className="truncate max-w-[200px]"
                                title={analysis.name}
                              >
                                {analysis.name}
                              </span>
                              <button
                                onClick={() => startEditing(analysis)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-40">
                          <div className="flex items-center">
                            <span 
                              className="truncate max-w-[120px]"
                              title={analysis.keyword}
                            >
                              {truncateKeyword(analysis.keyword)}
                            </span>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUrl === analysis.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="url"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                placeholder="Enter URL"
                                className="block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <button
                                onClick={() => handleSaveUrl(analysis.id, editingValue)}
                                className="text-green-600 hover:text-green-900"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              {analysis.url ? (
                                <>
                                  <a
                                    href={analysis.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                  <button
                                    onClick={() => {
                                      setEditingUrl(analysis.id);
                                      setEditingValue(analysis.url || '');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditingUrl(analysis.id);
                                    setEditingValue('');
                                  }}
                                  className="text-gray-400 hover:text-gray-600 flex items-center space-x-1"
                                >
                                  <Plus className="h-4 w-4" />
                                  <span className="text-sm">Add URL</span>
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTextScoreColor(analysis.text_score)}`}>
                            {getTextScoreDisplay(analysis.text_score)}
                          </span>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(analysis.created_at).toLocaleDateString()}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingStatus === analysis.id ? (
                            <div className="flex items-center space-x-2">
                              <select
                                value={analysis.status_type}
                                onChange={(e) => handleSaveStatus(analysis.id, e.target.value)}
                                className="block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              >
                                {STATUS_TYPES.map(status => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span 
                                className={`px-2 py-1 text-xs font-semibold rounded-full cursor-pointer ${getStatusColor(analysis.status_type)}`}
                                onClick={() => setEditingStatus(analysis.id)}
                              >
                                {analysis.status_type}
                              </span>
                            </div>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingDocsUrl === analysis.id ? (
                            <div className="flex items-center space-x-2">
                              <div className="relative flex-1">
                                <input
                                  type="url"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  placeholder="Enter Google Docs URL"
                                  className="block w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <button
                                    onClick={() => handleSaveDocsUrl(analysis.id, editingValue)}
                                    className="text-green-600 hover:text-green-900 p-1"
                                  >
                                    <Save className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="text-gray-600 hover:text-gray-900 p-1"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              {analysis.docs_url ? (
                                <>
                                  <a
                                    href={analysis.docs_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                  <button
                                    onClick={() => {
                                      setEditingDocsUrl(analysis.id);
                                      setEditingValue(analysis.docs_url || '');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditingDocsUrl(analysis.id);
                                    setEditingValue('');
                                  }}
                                  className="text-gray-400 hover:text-gray-600 flex items-center space-x-1"
                                >
                                  <Plus className="h-4 w-4" />
                                  <span className="text-sm">Add URL</span>
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <a
                            href={`/analyses/${analysis.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                          >
                            Open
                          </a>
                          <a
                            href={`/analyses/${analysis.id}/editor`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                          >
                            <PenSquare className="h-4 w-4 mr-1" />
                            Editor
                          </a>
                          {canDeleteAnalyses && (
                            <button
                              onClick={() => handleDeleteAnalysis(analysis.id)}
                              className="inline-flex items-center px-3 py-1.5 text-red-600 hover:text-red-900"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{" "}
                        <span className="font-medium">
                          {Math.min(currentPage * ITEMS_PER_PAGE, sortedAndFilteredAnalyses.length)}
                        </span>{" "}
                        of <span className="font-medium">{sortedAndFilteredAnalyses.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {/* Page numbers - optimized to show fewer buttons */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Always show first and last page
                            if (page === 1 || page === totalPages) return true;
                            
                            // Show pages around current page
                            if (Math.abs(page - currentPage) <= 1) return true;
                            
                            return false;
                          })
                          .map((page, index, filteredPages) => {
                            // Show ellipsis if there's a gap
                            const prevPage = index > 0 ? filteredPages[index - 1] : 0;
                            const showEllipsisBefore = index > 0 && page - prevPage > 1;
                            
                            return (
                              <div key={page} className="flex items-center">
                                {showEllipsisBefore && (
                                  <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => handlePageChange(page)}
                                  aria-current={currentPage === page ? "page" : undefined}
                                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                    currentPage === page
                                      ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                      : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0"
                                  }`}
                                >
                                  {page}
                                </button>
                              </div>
                            );
                          })}
                        
                        <button
                          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">No analyses found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}